import { useCallback } from "react";

import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { ONE_DAY_MS } from "@/constants/dateTime";
import {
  AdminAreaData,
  AdminAreaHierarchy,
  AdministrativeArea
} from "@/types/adminArea";
import { AdminAreaSettings } from "@/types/spatial";

import {
  getAdminAreaByIdApi,
  getAdminAreaHierarchy,
  getAllAdminAreaApi
} from "./adminAreaApi";

export type DataResponse = {
  id: string;
  name: string;
  province_id?: string;
};

export const ADMIN_AREA_KEYS = {
  lv0: ["lv0"] as const,
  lv1: ["lv1"] as const,
  lv2: ["lv2"] as const,
  lv3: ["lv3"] as const,
  lv4: ["lv3"] as const
} as const;

const getParentId = (level: number, address: AdminAreaSettings | null) => {
  switch (level) {
    case 1:
      return address?.lv1?.value;
    case 2:
      return address?.lv2?.value;
    case 3:
      return address?.lv3?.value;
    default:
      return address?.lv0?.value;
  }
};

const getQueryKey = (level: number, address: AdminAreaSettings | null) => {
  switch (level) {
    case 1:
      return [...ADMIN_AREA_KEYS.lv1, address?.lv1] as const;
    case 2:
      return [...ADMIN_AREA_KEYS.lv2, address?.lv2] as const;
    case 3:
      return [...ADMIN_AREA_KEYS.lv3, address?.lv3] as const;
    default:
      return [...ADMIN_AREA_KEYS.lv0, address?.lv0] as const;
  }
};

export const useAdmAreaQuery = (
  address: AdminAreaSettings | null,
  level: number,
  options?: Omit<
    Promise<unknown>[] | UseQueryOptions<AdminAreaData, Error, AdminAreaData>,
    "queryKey" | "queryFn"
  >
) => {
  const parentId = getParentId(level, address);

  return useQuery({
    queryKey: getQueryKey(level, address),
    queryFn: () => {
      if (!address?.lv0 || !parentId) return Promise.resolve(null);
      return getAllAdminAreaApi({
        country_id: address.lv0.value,
        parent_id: parentId,
        level,
        page: 1,
        per_page: 10
      });
    },
    enabled: !!address?.lv0 && !!getParentId(level, address),
    retry: 1,
    retryDelay: 1000,
    ...options
  });
};

export const useAdminAreaById = (
  id: string | null,
  zoomLevel?: number,
  options?: Omit<
    | Promise<unknown>[]
    | UseQueryOptions<AdminAreaData | null, Error, AdminAreaData | null>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AdminAreaData | null, Error> => {
  return useQuery({
    queryKey: ["admin-area-deatail", id, zoomLevel],
    queryFn: async () => {
      if (!id) return null;
      const response = await getAdminAreaByIdApi(id, zoomLevel);
      return {
        data: response ? [response] : []
      };
    },
    enabled: !!id,
    ...options
  });
};

export const useGetAdminAreaHierarchy = (
  countryId: string,
  options?: Omit<
    UseQueryOptions<
      { data: AdminAreaHierarchy[] } | null,
      Error,
      { data: AdminAreaHierarchy[] } | null
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<{ data: AdminAreaHierarchy[] } | null, Error> => {
  return useQuery({
    queryKey: ["admin-area-hierarchy", countryId],
    queryFn: async () => {
      const response = await getAdminAreaHierarchy(countryId);
      return response ?? null;
    },
    enabled: !!countryId,
    // Hierarchy data is static; match the persister maxAge so prefetched
    // data is reused without an instant refetch on consumer mount
    staleTime: ONE_DAY_MS,
    // Default 10min gcTime would evict observer-less prefetched queries
    // from memory and IndexedDB
    gcTime: ONE_DAY_MS,
    ...options,
    // After the spread so caller options cannot drop persistence
    meta: { shouldPersist: !!countryId, ...options?.meta }
  });
};

export const useFetchAdminAreaHierarchy = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (countryId: string): Promise<AdminAreaHierarchy[]> =>
      queryClient
        .fetchQuery({
          queryKey: ["admin-area-hierarchy", countryId],
          queryFn: () => getAdminAreaHierarchy(countryId),
          staleTime: Number.POSITIVE_INFINITY,
          gcTime: ONE_DAY_MS,
          // Query meta reflects the last fetcher's options — keep the persist
          // flag so this imperative path doesn't clobber it on the shared key
          meta: { shouldPersist: true }
        })
        .then((r) => r?.data ?? []),
    [queryClient]
  );
};

export const useFetchAdminAreasByIds = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (ids: string[]): Promise<(AdministrativeArea | null)[]> => {
      const queries = ids.map((id) => ({
        queryKey: ["admin-area-detail-query", id],
        queryFn: (): Promise<AdministrativeArea | null> =>
          getAdminAreaByIdApi(id),
        staleTime: Infinity,
        gcTime: Infinity
      }));

      return Promise.all(queries.map((query) => queryClient.fetchQuery(query)));
    },
    [queryClient]
  );
};
